import React, { useState, useEffect } from 'react';
import Modal from 'react-modal';
import { giftsAPI, creditsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import { FaTimes, FaGift, FaCoins, FaHeart, FaStar, FaGem } from 'react-icons/fa';

// Gift emoji mapping - updated to match new database gift_catalog order
const GIFT_EMOJIS = {
  // ID mappings (1-26 matching new database order by category)
  1: '🌹',   // Rose (romantic)
  2: '❤️',   // Heart (romantic)
  3: '🍷',   // Wine Bottle (romantic)
  4: '💍',   // Diamond Ring (premium)
  5: '👑',   // Crown (premium)
  6: '💍',   // Ring (premium)
  7: '🛥️',  // Yacht (premium)
  8: '🧴',   // Perfume (luxury)
  9: '🚗',   // Car (luxury)
  10: '🏰',  // Castle (luxury)
  11: '🧸',  // Teddy Bear (cute)
  12: '🍫',  // Chocolate Box (sweet)
  13: '🍦',  // Ice Cream (sweet)
  14: '🍕',  // Pizza (food)
  15: '🍔',  // Burger (food)
  16: '☕',  // Coffee (daily)
  17: '🍺',  // Beer (drink)
  18: '🍸',  // Cocktail (drink)
  19: '🎂',  // Cake (celebration)
  20: '🍾',  // Champagne (celebration)
  21: '🎆',  // Fireworks (celebration)
  22: '🎈',  // Balloon (fun)
  23: '🎉',  // Confetti (fun)
  24: '💄',  // Lipstick (beauty)
  25: '👠',  // Shoes (fashion)
  26: '✈️',  // Plane (travel)

  // Name mappings (exact match with database names)
  'Rose': '🌹',
  'Heart': '❤️',
  'Wine Bottle': '🍷',
  'Diamond Ring': '💍',
  'Crown': '👑',
  'Ring': '💍',
  'Yacht': '🛥️',
  'Perfume': '🧴',
  'Car': '🚗',
  'Castle': '🏰',
  'Teddy Bear': '🧸',
  'Chocolate Box': '🍫',
  'Ice Cream': '🍦',
  'Pizza': '🍕',
  'Burger': '🍔',
  'Coffee': '☕',
  'Beer': '🍺',
  'Cocktail': '🍸',
  'Cake': '🎂',
  'Champagne': '🍾',
  'Fireworks': '🎆',
  'Balloon': '🎈',
  'Confetti': '🎉',
  'Lipstick': '💄',
  'Shoes': '👠',
  'Plane': '✈️'
};

// Emoji mapping by sticker ID (pack_id + sticker_id format)
const STICKER_EMOJIS = {
  // Basic pack (pack_id = 1)
  '1-1': '😊',  // Happy
  '1-2': '😢',  // Sad
  '1-3': '😍',  // Love
  '1-4': '😠',  // Angry
  '1-5': '😮',  // Surprised
  '1-6': '😎',  // Cool
  '1-7': '😴',  // Sleepy
  '1-8': '😋',  // Hungry

  // Premium pack (pack_id = 2)
  '2-1': '🎉',  // Party
  '2-2': '🔥',  // Fire
  '2-3': '😍',  // Heart Eyes
  '2-4': '🤔',  // Thinking
  '2-5': '👍',  // Thumbs Up
  '2-6': '👏',  // Clap
  '2-7': '⭐',  // Star
  '2-8': '👑',  // Crown
  '2-9': '💎',  // Diamond
  '2-10': '🚀', // Rocket

  // VIP pack (pack_id = 3)
  '3-1': '👑',  // VIP Crown
  '3-2': '💎',  // Diamond
  '3-3': '💰',  // Money
  '3-4': '🚗',  // Car
  '3-5': '✈️',  // Plane
  '3-6': '🛥️',  // Yacht
  '3-7': '🏰',  // Castle
  '3-8': '🏆',  // Trophy
  '3-9': '🌟',  // Gold Star
  '3-10': '💎', // VIP
  '3-11': '🤑', // Luxury
  '3-12': '💎'  // Premium
};

// Helper function to get sticker emoji
export const getStickerEmoji = (packId, stickerId) => {
  const key = `${packId}-${stickerId}`;
  return STICKER_EMOJIS[key] || '😊'; // Default happy emoji
};

Modal.setAppElement('#root');

const GiftModal = ({ isOpen, onClose, profileId }) => {
  const { user, refreshCredits } = useAuth();
  const [selectedGifts, setSelectedGifts] = useState([]);
  const [sending, setSending] = useState(false);
  const [gifts, setGifts] = useState([]);
  const [loading, setLoading] = useState(false);

  // Modal açıldığında gifts catalog'u çek ve credits'i güncelle
  useEffect(() => {
    if (isOpen) {
      fetchGiftsCatalog();
      // Credits bilgisi AuthContext'ten geliyor (user.credits)
      // Modal açıldığında credits'i güncelle ki güncel olsun
      if (refreshCredits) {
        refreshCredits();
      }
    }
  }, [isOpen, refreshCredits]);

  // Listen for credits update events
  useEffect(() => {
    const handleCreditsUpdated = (event) => {
      // Force re-render by updating a dummy state
      setGifts(prev => [...prev]);
    };

    window.addEventListener('creditsUpdated', handleCreditsUpdated);

    return () => {
      window.removeEventListener('creditsUpdated', handleCreditsUpdated);
    };
  }, []);

  const fetchGiftsCatalog = async () => {
    try {
      setLoading(true);
      const response = await giftsAPI.getCatalog();
      if (response.data.success) {
        // Add emoji to each gift based on ID
        const giftsWithEmojis = response.data.gifts.map(gift => ({
          ...gift,
          emoji: GIFT_EMOJIS[gift.id] || '🎁' // Default gift emoji if not found
        }));
        setGifts(giftsWithEmojis);
      }
    } catch (error) {
      console.error('Failed to fetch gifts catalog:', error);
      // Fallback to default gifts if API fails (matching new database order)
      setGifts([
        { id: 1, name: 'Rose', emoji: '🌹', cost: 5, category: 'romantic' },
        { id: 2, name: 'Heart', emoji: '❤️', cost: 8, category: 'romantic' },
        { id: 3, name: 'Wine Bottle', emoji: '🍷', cost: 18, category: 'romantic' },
        { id: 4, name: 'Diamond Ring', emoji: '💍', cost: 25, category: 'premium' },
        { id: 5, name: 'Crown', emoji: '👑', cost: 30, category: 'premium' },
        { id: 6, name: 'Ring', emoji: '💍', cost: 35, category: 'premium' },
        { id: 7, name: 'Yacht', emoji: '🛥️', cost: 75, category: 'premium' },
        { id: 8, name: 'Perfume', emoji: '🧴', cost: 20, category: 'luxury' },
        { id: 9, name: 'Car', emoji: '🚗', cost: 50, category: 'luxury' },
        { id: 10, name: 'Castle', emoji: '🏰', cost: 100, category: 'luxury' },
        { id: 11, name: 'Teddy Bear', emoji: '🧸', cost: 15, category: 'cute' },
        { id: 12, name: 'Chocolate Box', emoji: '🍫', cost: 12, category: 'sweet' },
        { id: 13, name: 'Ice Cream', emoji: '🍦', cost: 4, category: 'sweet' },
        { id: 14, name: 'Pizza', emoji: '🍕', cost: 8, category: 'food' },
        { id: 15, name: 'Burger', emoji: '🍔', cost: 7, category: 'food' },
        { id: 16, name: 'Coffee', emoji: '☕', cost: 6, category: 'daily' },
        { id: 17, name: 'Beer', emoji: '🍺', cost: 9, category: 'drink' },
        { id: 18, name: 'Cocktail', emoji: '🍸', cost: 11, category: 'drink' },
        { id: 19, name: 'Cake', emoji: '🎂', cost: 14, category: 'celebration' },
        { id: 20, name: 'Champagne', emoji: '🍾', cost: 28, category: 'celebration' },
        { id: 21, name: 'Fireworks', emoji: '🎆', cost: 20, category: 'celebration' },
        { id: 22, name: 'Balloon', emoji: '🎈', cost: 3, category: 'fun' },
        { id: 23, name: 'Confetti', emoji: '🎉', cost: 6, category: 'fun' },
        { id: 24, name: 'Lipstick', emoji: '💄', cost: 16, category: 'beauty' },
        { id: 25, name: 'Shoes', emoji: '👠', cost: 22, category: 'fashion' },
        { id: 26, name: 'Plane', emoji: '✈️', cost: 40, category: 'travel' },
      ]);
    } finally {
      setLoading(false);
    }
  };


  // Hediye seçme/toggle etme fonksiyonu
  const toggleGiftSelection = (gift) => {
    setSelectedGifts(prev => {
      const isSelected = prev.find(g => g.id === gift.id);
      if (isSelected) {
        // Seçiliyse çıkar
        return prev.filter(g => g.id !== gift.id);
      } else {
        // Seçili değilse ekle
        return [...prev, gift];
      }
    });
  };

  // Toplam maliyet hesaplama
  const totalCost = selectedGifts.reduce((sum, gift) => sum + gift.cost, 0);


  const handleSendGift = async () => {
    if (selectedGifts.length === 0) {
      toast.warning('Please select at least one gift to send');
      return;
    }

    if ((user?.credits || 0) < totalCost) {
      toast.error(`Insufficient credits! You need ${totalCost} credits but only have ${user?.credits || 0}`);
      return;
    }

    try {
      setSending(true);

      // Çoklu hediye gönderme
      for (const gift of selectedGifts) {
        await giftsAPI.sendGift({
          profile_id: profileId,
          gift_id: gift.id,
          gift_name: gift.name, // Include gift name
          gift_path: gift.id.toString(), // Use gift ID as path
          cost: gift.cost
        });
      }

      const giftNames = selectedGifts.map(g => g.name).join(', ');
      toast.success(`🎁 ${selectedGifts.length > 1 ? `${selectedGifts.length} gifts` : selectedGifts[0].name} sent successfully!`);

      // Refresh credits from database and wait for it to complete
      if (refreshCredits) {
        await refreshCredits();
      }

      onClose();
      setSelectedGifts([]);
    } catch (error) {
      toast.error('Failed to send gift. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'romantic': return <FaHeart className="text-red-500" />;
      case 'premium': return <FaGem className="text-purple-500" />;
      case 'luxury': return <FaStar className="text-yellow-500" />;
      case 'cute': return <FaHeart className="text-pink-500" />;
      case 'sweet': return <span className="text-orange-500">🍬</span>;
      case 'fun': return <span className="text-green-500">🎈</span>;
      case 'daily': return <span className="text-amber-600">☕</span>;
      case 'celebration': return <span className="text-blue-500">🎉</span>;
      case 'beauty': return <span className="text-rose-500">💄</span>;
      case 'fashion': return <span className="text-indigo-500">👠</span>;
      case 'travel': return <span className="text-teal-500">✈️</span>;
      case 'food': return <span className="text-orange-500">🍕</span>;
      case 'drink': return <span className="text-blue-400">🍸</span>;
      default: return <FaGift className="text-blue-500" />;
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'romantic': return 'border-red-200 bg-red-50';
      case 'premium': return 'border-purple-200 bg-purple-50';
      case 'luxury': return 'border-yellow-200 bg-yellow-50';
      case 'cute': return 'border-pink-200 bg-pink-50';
      case 'sweet': return 'border-orange-200 bg-orange-50';
      case 'fun': return 'border-green-200 bg-green-50';
      case 'daily': return 'border-amber-200 bg-amber-50';
      case 'celebration': return 'border-blue-200 bg-blue-50';
      case 'beauty': return 'border-rose-200 bg-rose-50';
      case 'fashion': return 'border-indigo-200 bg-indigo-50';
      case 'travel': return 'border-teal-200 bg-teal-50';
      case 'food': return 'border-orange-200 bg-orange-50';
      case 'drink': return 'border-blue-200 bg-blue-50';
      default: return 'border-gray-200 bg-gray-50';
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      className="matchus-gift-modal"
      overlayClassName="matchus-modal-overlay"
    >
      <div className="matchus-gift-modal-content">
        <div className="matchus-gift-modal-header">
          <div className="matchus-gift-modal-title">
            {getCategoryIcon('romantic')}
            <div>
              <h2>Send a Gift</h2>
              <p>Choose a special gift to brighten someone's day</p>
            </div>
          </div>
          <button
            type="button"
            className="matchus-gift-modal-close"
            onClick={onClose}
          >
            <FaTimes />
          </button>
        </div>

        <div className="matchus-gift-modal-body">
          <div className="matchus-gift-info">
            <div className="matchus-gift-cost">
              <FaCoins className="matchus-gift-cost-icon" />
              <span>{user?.credits || 0} Credits</span>
            </div>
            {selectedGifts.length > 0 && (
              <div className="matchus-gift-selection-info">
                <span className="matchus-selected-count">
                  {selectedGifts.length} gift{selectedGifts.length > 1 ? 's' : ''} selected
                </span>
                <span className="matchus-total-cost">
                  Total: {totalCost} credits
                </span>
              </div>
            )}
          </div>

          {/* Tüm hediyeler tek grid'de */}
          <div className="matchus-gift-grid">
            {gifts.map((gift) => {
              const isSelected = selectedGifts.some(g => g.id === gift.id);
              return (
                <div
                  key={gift.id}
                  className={`matchus-gift-item ${isSelected ? 'selected' : ''} ${getCategoryColor(gift.category)}`}
                  onClick={() => toggleGiftSelection(gift)}
                >
                  <div className="matchus-gift-icon-wrapper">
                    <div className="matchus-gift-emoji">{gift.emoji}</div>
                    {isSelected && (
                      <div className="matchus-gift-selected-indicator">
                        ✓
                      </div>
                    )}
                  </div>
                  <div className="matchus-gift-name">{gift.name}</div>
                  <div className="matchus-gift-price">
                    <FaCoins className="text-yellow-500 text-xs" />
                    <span>{gift.cost}</span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Loading and empty states */}
          {loading && (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pink-500 mx-auto mb-2"></div>
              <p className="text-gray-500">Loading gifts...</p>
            </div>
          )}

          {!loading && gifts.length === 0 && (
            <div className="text-center py-8">
              <FaGift className="h-12 w-12 text-gray-300 mx-auto mb-2" />
              <p className="text-gray-500">No gifts available</p>
            </div>
          )}
        </div>

        <div className="matchus-gift-modal-footer">
          <button
            type="button"
            className="matchus-gift-cancel-btn"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className="matchus-gift-send-btn"
            onClick={handleSendGift}
            disabled={sending || selectedGifts.length === 0}
          >
            {sending ? (
              <>
                <div className="matchus-gift-spinner"></div>
                <span>Sending...</span>
              </>
            ) : (
              <>
                <FaGift />
                <span>Send Gift</span>
              </>
            )}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default GiftModal;

